import { useMemo } from 'react';
import { useLanguage } from './useLanguage';
import { LocalizedValue } from '../Model/LocalizedValue';

const languagePriority = ['de', 'en', 'fr', 'it'];

/**
 * Returns the localized string from a LocalizedValue array based on current language.
 *
 * @example
 * const name = useLocalized(entity.name);
 * const description = useLocalized(entity.description);
 */
export function useLocalized(values: LocalizedValue[] | undefined | null, fallback = ''): string {
  const { language } = useLanguage();

  return useMemo(() => {
    if (!values || values.length === 0) {
      return fallback;
    }

    const langLower = language.toLowerCase();
    const langFit = values.find((v) => v.lang === langLower)?.value;

    if (langFit?.trim()) {
      return langFit;
    }

    for (const lang of languagePriority) {
      const entry = values.find((v) => v.lang === lang);
      if (entry?.value) {
        return entry.value;
      }
    }

    return values[0]?.value ?? fallback;
  }, [values, language, fallback]);
}
